import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Slider from 'react-slick';

import { IconButton } from 'components/components/atoms';
import TimelineMediaSlide from 'components/components/molecules/TimelineMediaSlide';
import TimelineSlide from 'components/components/molecules/TimelineSlide';
import TimelineTextSlide from 'components/components/molecules/TimelineTextSlide';

import breakpoints from 'utils/breakpoints';
import useBreakpoint from 'utils/hooks/useBreakpoint';

const Container = styled.div`
  min-height: 280px;
  & > div > div:nth-child(2) {
    z-index: 1;
    height: 100%;
  }
`;

// 2, 4,
const DotCircle = styled.div`
  ${({ numItems }) => numItems && numItems === 3 && ' margin-left: 1.05%;'}
  ${({ numItems }) => numItems && numItems === 2 && ' margin-left: 5%;'}
  ${({ numItems }) => numItems && numItems === 1 && ' margin-left: 10%;'}

  @media ${breakpoints.lg} {
    ${({ numItems }) => numItems && numItems === 5 && ' margin-left: 1%;'}
    ${({ numItems }) => numItems && numItems === 4 && ' margin-left: 11.5%;'}
    ${({ numItems }) => numItems && numItems === 3 && ' margin-left: 10.5%;'} 
    ${({ numItems }) => numItems && numItems === 2 && ' margin-left: 24%;'}
    ${({ numItems }) => numItems && numItems === 1 && ' margin-left: 45%;'}
  }
`;

const CustomArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  &:before {
    display: none;
  }
`;

const CustomNextArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <CustomArrowContainer
      className={cx('mr-12', className)}
      style={{ ...style, marginRight: '2.8rem' }}
      onClick={onClick}
    >
      <IconButton
        className={cx(
          'absolute hidden md:flex z-20 transition-opacity duration-200 -mt-4',
        )}
        icon='arrow-right'
        idIcon={'TimelineCarousel'}
        color='mattamy-blue'
        circled
        hoverState
        buttonLabel='Next slide on gallery carousel'
        title={'Right arrow icon'}
        small
      />
    </CustomArrowContainer>
  );
};

const CustomPrevArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <CustomArrowContainer
      className={cx('', className)}
      style={{ ...style, marginLeft: '1.64rem' }}
      onClick={onClick}
    >
      <IconButton
        className='absolute hidden md:flex z-20 transition-opacity duration-200 -mt-4'
        icon='arrow-left'
        idIcon={'TimelineCarousel'}
        color='mattamy-blue'
        circled
        hoverState
        buttonLabel='Previous slide on gallery carousel'
        title={'Left arrow icon'}
        small
      />
    </CustomArrowContainer>
  );
};

const CustomDots = ({ index, position, labelName, maxLen, isMobile }) => {
  return (
    <div className='w-full flex flex-col pt-2 md:pt-6 text-center'>
      {index === position && (
        <DotCircle
          style={{
            transform: !isMobile ? 'translateX(35px)' : 'translateX(15px)',
          }}
          numItems={maxLen}
          className={cx(
            'rounded-full w-4 h-4 md:w-6 md:h-6 bg-mattamy-blue absolute top-0 mt-0 md:mt-3',
          )}
        ></DotCircle>
      )}
      <div
        className={cx('font-graphie text-sm pt-2 md:pt-3 text-center px-5', {
          'border-t-2': true,
          'text-mattamy-gray-light': index !== position,
          'text-mattamy-blue font-bold border-mattamy-blue ml-2 ':
            index === position,
        })}
      >
        <span className='text-center md:ml-4'>{labelName}</span>
      </div>
    </div>
  );
};

const TimelineGallery = ({ events = [] }) => {
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const len = events.length;
  const stepsToShowMobile = len >= 3 ? 3 : len;
  const stepsToShowDesktop = len >= 5 ? 5 : len;
  const [position, setPosition] = useState(0);
  const [slidesMedia, setSlidesMedia] = useState();
  const [slidesMobile, setSlidesMobile] = useState();
  const [slidesSteps, setSlidesSteps] = useState();

  useEffect(() => {
    // onChange((len - Math.abs(position % len)) % len);
  }, [position, len]);

  const sliderMobileSettings = {
    className: 'slider w-full h-auto', // variable-width
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '30px',
    swipeToSlide: true,
    touchThreshold: 100,
    afterChange: (current) => {
      setPosition(current);
    },
    ref: (slider) => setSlidesMobile(slider),
    asNavFor: slidesSteps,
  };

  const sliderMediaSettings = {
    className: 'slider w-full h-auto', // variable-width
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: isMobile,
    centerPadding: '16px',
    swipeToSlide: true,
    touchThreshold: 100,
    beforeChange: (oldIndex, newIndex) => {
      const iframe = document.querySelector(
        `.slick-slide[data-index="${oldIndex}"] iframe`,
      );

      if (iframe && iframe.src.includes('vimeo.com')) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: 'pause' }),
          '*',
        );
      }
    },
    afterChange: (current) => {
      setPosition(current);
    },
    ref: (slider) => setSlidesMedia(slider),
    asNavFor: slidesSteps, // slidesText,
  };

  const sliderStepsSettings = {
    className: 'slider w-full h-auto', // variable-width
    dots: false,
    infinite: true,
    slidesToShow: isMobile ? stepsToShowMobile : stepsToShowDesktop,
    slidesToScroll: 1,
    centerMode: false,
    centerPadding: isMobile ? '5px' : '10px',
    swipeToSlide: true,
    touchThreshold: 100,
    focusOnSelect: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    ref: (slider) => setSlidesSteps(slider),
    asNavFor: isMobile ? slidesMobile : slidesMedia,
  };
  const timelineText = events && events.length > 0 ? events[position] : {};

  return (
    <>
      <Container
        className={cx(
          'w-full md:rounded overflow-hidden relative flex flex-col items-center justify-center mt-16',
        )}
      >
        {!isMobile && (
          <div className='w-full flex flex-col md:flex-row mb-8 md:mb-12'>
            <div className='w-full md:w-1/2 self-center -mt-8 md:pr-8'>
              <TimelineTextSlide
                key={timelineText.labelName}
                index={position}
                labelName={timelineText.labelName}
                title={timelineText.title}
                text={timelineText.text}
              />
            </div>
            <div className='w-full md:w-1/2'>
              <Slider {...sliderMediaSettings}>
                {events &&
                  events.map((e, i) => (
                    <TimelineMediaSlide
                      key={e.labelName}
                      index={i}
                      position={position}
                      image={e.image}
                      video={e.video}
                      transcript={e.transcript}
                      transcriptText={e.transcriptText}
                    />
                  ))}
              </Slider>
            </div>
          </div>
        )}

        {isMobile && (
          <Slider {...sliderMobileSettings}>
            {events &&
              events.map((e, i) => (
                <TimelineSlide
                  key={e.labelName}
                  index={i}
                  position={position}
                  labelName={e.labelName}
                  title={e.title}
                  text={e.text}
                  image={e.image}
                  video={e.video}
                  transcript={e.transcript}
                  transcriptText={e.transcriptText}
                />
              ))}
          </Slider>
        )}

        <Slider {...sliderStepsSettings}>
          {events &&
            events.map((e, i) => (
              <CustomDots
                key={e.labelName}
                index={i}
                position={position}
                maxLen={isMobile ? stepsToShowMobile : stepsToShowDesktop}
                labelName={e.labelName}
                isMobile={isMobile}
              />
            ))}
        </Slider>
      </Container>
    </>
  );
};

TimelineGallery.propTypes = {
  carousel: PropTypes.array.isRequired,
  ctas: PropTypes.array.isRequired,
  className: PropTypes.string,
  showFavorites: PropTypes.bool,
  showShare: PropTypes.bool,
  availableDateMessage: PropTypes.string,
  modelHome: PropTypes.string,
  onChange: PropTypes.func,
  handleImageClick: PropTypes.func,
  handleFilterChange: PropTypes.func,
};

export default TimelineGallery;
